/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { drawDryAdiabats, drawDryAdiabatsBijvoet } from './drawDryAdiabats';
import { drawIsobars } from './drawIsobars';
import { drawIsohumes, drawIsohumesBijvoet } from './drawIsohumes';
import { drawIsotherms, drawIsothermsBijvoet } from './drawIsotherms';
import {
  drawMoistAdiabats,
  drawMoistAdiabatsBijvoet,
} from './drawMoistAdiabats';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import type { ScalesType } from '../Soundings/types';

interface DrawStaticLinesProps {
  diagramType: SoundingDiagramType;
  dp: number;
  staticPressureArray: number[];
  minTemperature: number;
  maxTemperature: number;
  minPressure: number;
  maxPressure: number;
  width: number;
  height: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  d3GraphId: string; // used to namespace clip paths per instance
}

export const drawStaticLines = ({
  diagramType,
  dp,
  staticPressureArray,
  minTemperature,
  maxTemperature,
  height,
  width,
  xScale,
  yScale,
  minPressure,
  maxPressure,
  svg,
  d3GraphId,
}: DrawStaticLinesProps): void => {
  drawIsobars({
    diagramType,
    minTemperature,
    maxTemperature,
    xScale,
    yScale,
    svg,
  });

  if (diagramType === 'BIJVOET') {
    drawMoistAdiabatsBijvoet({
      minPressure,
      maxPressure,
      xScale,
      yScale,
      svg,
      d3GraphId,
    });

    drawIsothermsBijvoet({
      staticPressureArray,
      xScale,
      yScale,
      svg,
      height,
      d3GraphId,
    });

    drawDryAdiabatsBijvoet({
      staticPressureArray,
      xScale,
      yScale,
      svg,
      d3GraphId,
    });

    drawIsohumesBijvoet({
      staticPressureArray,
      height,
      xScale,
      yScale,
      svg,
      d3GraphId,
    });
  } else {
    drawIsotherms({
      diagramType,
      staticPressureArray,
      height,
      xScale,
      yScale,
      svg,
      d3GraphId,
    });

    drawMoistAdiabats({
      diagramType,
      staticPressureArray,
      minTemperature,
      maxTemperature,
      minPressure,
      maxPressure,
      width,
      height,
      xScale,
      yScale,
      svg,
      dp,
      d3GraphId,
    });

    drawDryAdiabats({
      diagramType,
      staticPressureArray,
      height,
      xScale,
      yScale,
      svg,
      dp,
      d3GraphId,
    });

    drawIsohumes({
      diagramType,
      staticPressureArray,
      height,
      xScale,
      yScale,
      svg,
      d3GraphId,
    });
  }
};
